// src/services/publicSeoService.ts
import axios from 'axios';
import { getBaseUrl } from '../config/api';
import type { PublicStylist, SeoInventory } from '../seo/types';

/**
 * Public marketplace data for the unauthenticated SEO pages.
 *
 * Deliberately a bare axios instance rather than the shared `apiClient`: these
 * pages are browsed by logged-out visitors and crawlers, and they must never
 * trigger the session-refresh / 401-retry machinery that `apiClient` layers on
 * every request. No credentials are sent, so responses stay edge-cacheable.
 */
const publicClient = axios.create({
  baseURL: getBaseUrl(),
  timeout: 12000,
  withCredentials: false,
  headers: { 'X-Client-Platform': 'web' },
});

export interface PublicDirectoryFilters {
  state?: string;
  city?: string;
  serviceCategory?: string;
  limit?: number;
}

export interface PublicDirectoryResult {
  total: number;
  stylists: PublicStylist[];
  priceMin?: number;
  priceMax?: number;
}

const EMPTY_DIRECTORY: PublicDirectoryResult = { total: 0, stylists: [] };

/**
 * A public landing page must still render when the API is unreachable — it
 * degrades to its "no inventory yet" state (which is noindex anyway) rather
 * than showing an error screen to a crawler.
 */
export async function getPublicDirectory(
  filters: PublicDirectoryFilters
): Promise<PublicDirectoryResult> {
  try {
    const response = await publicClient.get('/public/seo/directory', { params: filters });
    return (response.data?.data as PublicDirectoryResult) || EMPTY_DIRECTORY;
  } catch (error) {
    return EMPTY_DIRECTORY;
  }
}

/**
 * @returns the public profile, or null when the slug is unknown / not public.
 */
export async function getPublicStylist(seoSlug: string): Promise<PublicStylist | null> {
  try {
    const response = await publicClient.get(
      `/public/seo/stylists/${encodeURIComponent(seoSlug)}`
    );
    return (response.data?.data as PublicStylist) || null;
  } catch (error) {
    return null;
  }
}

/**
 * Aggregated national inventory. Used by the location/service hub pages and by
 * the build-time sitemap generator (which calls the same endpoint over HTTP).
 */
export async function getSeoInventory(): Promise<SeoInventory | null> {
  try {
    const response = await publicClient.get('/public/seo/inventory');
    return (response.data?.data as SeoInventory) || null;
  } catch (error) {
    return null;
  }
}

const publicSeoService = {
  getPublicDirectory,
  getPublicStylist,
  getSeoInventory,
};

export default publicSeoService;
